import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';

export interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  minWidth: number;
  inputBg: string;
  border: string;
  text: string;
  muted: string;
  activeColor: string;
  panelBg: string;
  rowHover: string;
  /** When false, omit the placeholder/clear row (required pick). Default true. */
  allowEmpty?: boolean;
  /** When false, dropdown is a plain list with no search field. Default true. */
  searchable?: boolean;
}

/** Compact searchable select used on Inventory / Requests filter bars. */
const FilterSelect: React.FC<FilterSelectProps> = ({
  value,
  onChange,
  placeholder,
  options,
  minWidth,
  inputBg,
  border,
  text,
  muted,
  activeColor,
  panelBg,
  rowHover,
  allowEmpty = true,
  searchable = true,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const active = !!value;
  const selectedLabel = options.find(o => o.value === value)?.label ?? '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const updateMenuPos = () => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const menuMaxH = 260;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(menuMaxH, openUp ? spaceAbove - 8 : spaceBelow - 8));
    const top = openUp ? Math.max(8, rect.top - gap - maxHeight) : rect.bottom + gap;
    const width = Math.max(rect.width, minWidth, 180);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    setMenuPos({ top, left, width, maxHeight });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      window.removeEventListener('scroll', updateMenuPos, true);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [open, filtered.length, minWidth]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  const optionStyle = (isSelected: boolean) => ({
    color: isSelected ? activeColor : text,
    backgroundColor: isSelected ? rowHover : 'transparent',
  });

  const menu = open && menuPos
    ? createPortal(
        <div
          ref={menuRef}
          className="fixed z-[80] rounded-md shadow-xl overflow-hidden flex flex-col"
          style={{
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: menuPos.maxHeight,
            backgroundColor: panelBg,
            border: `1px solid ${border}`,
          }}
        >
          {searchable && (
            <div className="p-1.5 flex-shrink-0" style={{ borderBottom: `1px solid ${border}` }}>
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: muted }} />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full text-xs pl-7 pr-2 py-1 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                  style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                />
              </div>
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
            {allowEmpty && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className="w-full text-left px-2 py-1.5 text-xs transition-colors"
                style={optionStyle(!value)}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = rowHover; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = !value ? rowHover : 'transparent'; }}
              >
                {placeholder}
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="px-2 py-2 text-xs text-center" style={{ color: muted }}>No matches</div>
            ) : (
              filtered.map(o => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => handleSelect(o.value)}
                  className="w-full text-left px-2 py-1.5 text-xs truncate transition-colors"
                  style={optionStyle(o.value === value)}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = rowHover; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = o.value === value ? rowHover : 'transparent'; }}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="relative" style={{ minWidth }} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full box-border inline-flex items-center text-xs font-medium rounded px-3 py-1 pr-6 border text-left truncate focus:outline-none h-[26px]"
        style={{
          backgroundColor: inputBg,
          borderColor: border,
          color: active ? activeColor : text,
          minWidth,
        }}
      >
        {active ? selectedLabel : placeholder}
      </button>
      <ChevronDown
        size={12}
        className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${open ? 'rotate-180' : ''}`}
        style={{ color: muted }}
      />
      {menu}
    </div>
  );
};

export default FilterSelect;
